import apiClient from "@/api/apiClient";
import { deleteMaintenanceItemToPart } from "@/api/resources/maintenanceItemsToParts/api";
import {
  MaintenanceItemStatus,
  type ArgsExportMaintenanceForCsv,
  type ArgsExportMaintenanceItemForCsv,
  type MaintenanceItem,
  type UpdateMaintenanceItem,
} from "@/supabase.types";
import {
  spawnCreateApiFunction,
  spawnDeleteApiFunction,
  spawnGetApiFunction,
  spawnUpdateApiFunction,
  type ApiResourceConfig,
} from "@/utils/api";
import { getDateNow } from "@/utils/dates";

const table = "maintenance_items";
export const query = "*";

export const apiConfigMaintenanceItems: ApiResourceConfig<typeof table, typeof query> = {
  table,
  query,
  searchables: ["name", "description", "notes"],
  sortables: ["name", "updated_at", "created_at", "started_at", "resolved_at", "ordering", "is_custom", "status"],
};

export const getMaintenanceItem = spawnGetApiFunction({ table, query });
export const createMaintenanceItem = spawnCreateApiFunction({ table, query });
export const updateMaintenanceItem = spawnUpdateApiFunction({ table, query });
export const deleteMaintenanceItem = spawnDeleteApiFunction({ table });

export const getMaintenanceItemsByMaintenanceId = async (maintenance_id: string) => {
  const { data, error } = await apiClient.from(table).select(query).eq("maintenance_id", maintenance_id);
  if (error) throw error;
  return data;
};

export const exportMaintenancesForCsv = async (props: ArgsExportMaintenanceForCsv) => {
  const { data, error } = await apiClient.rpc("export_maintenances_for_csv", props).csv();
  if (error) throw error;
  if (data) return data;
};
export const exportMaintenanceItemsForCsv = async (props: ArgsExportMaintenanceItemForCsv) => {
  const { data, error } = await apiClient.rpc("export_maintenance_items_for_csv", props).csv();
  if (error) throw error;
  if (data) return data;
};

export const applySparePartUsage = async (maintenance_item_id: string) => {
  const { error } = await apiClient.rpc("apply_spare_part_usage", {
    _maintenance_item_id: maintenance_item_id,
  });
  if (error) throw error;
};

interface SkipRemainingMaintenanceItemsProps {
  maintenanceItems: MaintenanceItem[];
  userId: string;
}
export const skipRemainingMaintenanceItems = async ({
  maintenanceItems,
  userId,
}: SkipRemainingMaintenanceItemsProps) => {
  const updateState: UpdateMaintenanceItem = {
    started_at: null,
    resolved_at: getDateNow(),
    status: MaintenanceItemStatus.skipped,
    user_id: userId,
  };
  const items_pending: UpdateMaintenanceItem[] = maintenanceItems
    .filter((item) => item.status === MaintenanceItemStatus.pending)
    .map(({ id, notes }) => ({
      id,
      notes,
      ...updateState,
    }));
  const items_started: UpdateMaintenanceItem[] = maintenanceItems
    .filter((item) => item.status === MaintenanceItemStatus.started)
    .map(({ id, notes }) => ({
      id,
      notes,
      ...updateState,
    }));

  const results = await Promise.all(
    [...items_pending, ...items_started].map((item) =>
      // update all items that are pending or started
      updateMaintenanceItem({
        match: { id: item.id },
        values: item,
      }).then(() => {
        // delete all used parts, if there are any used in pending or started items
        void deleteMaintenanceItemToPart({
          match: { maintenance_item_id: item.id },
        });
      }),
    ),
  );
  return results;
};

interface FailRemainingMaintenanceItemsProps {
  maintenanceItems: MaintenanceItem[];
  userId: string;
}
export const failRemainingMaintenanceItems = async ({
  maintenanceItems,
  userId,
}: FailRemainingMaintenanceItemsProps) => {
  const updateState: UpdateMaintenanceItem = {
    resolved_at: getDateNow(),
    status: MaintenanceItemStatus.failed,
    user_id: userId,
  };
  const items_pending: UpdateMaintenanceItem[] = maintenanceItems
    .filter((item) => item.status === MaintenanceItemStatus.pending)
    .map(({ id, notes }) => ({
      id,
      notes,
      started_at: getDateNow(),
      ...updateState,
    }));
  const items_started: UpdateMaintenanceItem[] = maintenanceItems
    .filter((item) => item.status === MaintenanceItemStatus.started)
    .map(({ id, notes }) => ({
      id,
      notes,
      ...updateState,
    }));

  const results = await Promise.all(
    [...items_pending, ...items_started].map((item) =>
      updateMaintenanceItem({
        match: { id: item.id },
        values: item,
      }).then(() => {
        // delete all used parts, if there are any used in pending or started items
        void deleteMaintenanceItemToPart({
          match: { maintenance_item_id: item.id },
        });
      }),
    ),
  );
  return results;
};

interface FinishRemainingMaintenanceItemsProps {
  maintenanceItems: MaintenanceItem[];
  userId: string;
}
export const finishRemainingMaintenanceItems = async ({
  maintenanceItems,
  userId,
}: FinishRemainingMaintenanceItemsProps) => {
  const updateState: UpdateMaintenanceItem = {
    resolved_at: getDateNow(),
    status: MaintenanceItemStatus.completed,
    user_id: userId,
  };
  const items_pending: UpdateMaintenanceItem[] = maintenanceItems
    .filter((item) => item.status === MaintenanceItemStatus.pending)
    .map(({ id, notes }) => ({
      id,
      notes,
      started_at: getDateNow(),
      ...updateState,
    }));
  const items_started: UpdateMaintenanceItem[] = maintenanceItems
    .filter((item) => item.status === MaintenanceItemStatus.started)
    .map(({ id, notes }) => ({
      id,
      notes,
      ...updateState,
    }));

  const results = await Promise.all(
    [...items_pending, ...items_started].map((item) =>
      updateMaintenanceItem({
        match: { id: item.id },
        values: item,
      }).then(() => {
        // delete all used parts, if there are any used in pending or started items
        void deleteMaintenanceItemToPart({
          match: { maintenance_item_id: item.id },
        });
      }),
    ),
  );
  return results;
};

export const exportMaintenanceItemsCsv = async (args: ArgsExportMaintenanceItemForCsv) => {
  const { error, ...rest } = await apiClient.rpc("export_maintenance_items_for_csv", args).csv();
  if (error) throw error;

  return { error, ...rest };
};
